import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { apiService } from '../../../services/api';
import {
  DEFAULT_LOGO_STYLE,
  LOGO_BOX_PADDING_RATIO,
  LOGO_BOX_RADIUS_RATIO,
  LOGO_CORNERS,
  LOGO_CORNER_RADIUS_RATIO,
  LOGO_MARGIN_RATIO,
  LOGO_POSITION_LABELS,
  LOGO_SIZES,
  sameLogoStyle,
  type ApplyLogoResponse,
  type LogoCorner,
  type LogoCornersResponse,
  type LogoPosition,
  type LogoSize,
  type LogoStyle,
} from '../../../types/logoPlacement';

interface LogoPlacementSectionProps {
  postId: string;
  rawImageUrl: string;
  logoPosition: LogoPosition | null;
  corners: LogoCornersResponse | null;
  cornersError: string | null;
  cornersLoading: boolean;
  onApplied: (view: ApplyLogoResponse) => void;
  onCornerSelect?: (corner: LogoPosition | null) => void;
  /** The chosen logo style (which logo, size, background, rounding), so save/publish can apply it too. */
  onStyleChange?: (style: LogoStyle) => void;
  /** True when this post is a Carousel — applies go through the carousel
   * apply-logo endpoint instead of the static-post one, and require
   * slideNumber (each slide's corner is independent). */
  isCarousel?: boolean;
  /** 1-based. Required when isCarousel is true. */
  slideNumber?: number;
  /** Carousel: how many slides it has (for the label and the apply-to-all option). */
  totalSlides?: number;
}

// Where each corner's clickable zone sits over the thumbnail.
const ZONE_CLASSES: Record<LogoCorner, string> = {
  top_left: 'top-1.5 left-1.5',
  top_right: 'top-1.5 right-1.5',
  bottom_left: 'bottom-1.5 left-1.5',
  bottom_right: 'bottom-1.5 right-1.5',
};

// The selected zone's labels move to the edge away from the logo preview so
// they never sit under the logo.
const SELECTED_LABELS_ALIGN: Record<LogoCorner, string> = {
  top_left: 'justify-end',
  top_right: 'justify-end',
  bottom_left: 'justify-start',
  bottom_right: 'justify-start',
};

const FIXED_BACKGROUNDS = [
  { colour: '#FFFFFF', label: 'White' },
  { colour: '#000000', label: 'Black' },
];

/**
 * "Logo placement" block for logo-free static posts (AI Content Editor → Media).
 * The parent decides visibility and owns the corner scores (see useLogoCorners),
 * so the publish guard works without this tab being opened.
 */
export default function LogoPlacementSection({
  postId,
  rawImageUrl,
  logoPosition,
  corners,
  cornersError,
  cornersLoading,
  onApplied,
  onCornerSelect,
  onStyleChange,
  isCarousel = false,
  slideNumber,
  totalSlides,
}: LogoPlacementSectionProps) {
  const current: LogoPosition | null = logoPosition ?? corners?.logo_position ?? null;
  const currentStyle: LogoStyle = { ...DEFAULT_LOGO_STYLE, ...(corners?.logo_style ?? {}) };
  const [selected, setSelected] = useState<LogoPosition | null>(current ?? corners?.recommended ?? null);
  const [style, setStyle] = useState<LogoStyle>(currentStyle);
  // The preview logo's natural aspect (height / width) and the thumbnail's
  // rendered size, so the preview logo's size, margin, box padding and corner
  // radii match the composite's.
  const [logoAspect, setLogoAspect] = useState<number | null>(null);
  const [frame, setFrame] = useState<{ w: number; h: number } | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  // Carousel: keep the logo the same on every slide unless the user opts out.
  const [applyToAllSlides, setApplyToAllSlides] = useState(true);
  const canApplyToAll = isCarousel && (totalSlides ?? 0) > 1;
  const allSlides = canApplyToAll && applyToAllSlides;
  const gridRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const colourInputRef = useRef<HTMLInputElement>(null);

  // The thumbnail renders only once the corner scores are in, so track it
  // through a callback ref rather than a mount effect.
  const setGridEl = useCallback((el: HTMLDivElement | null) => {
    gridRef.current = el;
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setFrame({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    observer.observe(el);
    resizeObserverRef.current = observer;
  }, []);

  // Pre-select the current corner, else the recommended one, and the current
  // style — re-evaluated only when a new set of scores arrives (e.g. after
  // regenerate/select/enhance), not after an apply, so the user's selection
  // stays put.
  useEffect(() => {
    const initialCorner = current ?? corners?.recommended ?? null;
    setSelected(initialCorner);
    onCornerSelect?.(initialCorner);
    setStyle(currentStyle);
    onStyleChange?.(currentStyle);
    setApplyError(null);
  }, [corners]);

  const handleSelectCorner = (corner: LogoPosition) => {
    setSelected(corner);
    onCornerSelect?.(corner);
  };

  const updateStyle = (patch: Partial<LogoStyle>) => {
    const next = { ...style, ...patch };
    setStyle(next);
    onStyleChange?.(next);
  };

  if (cornersError) {
    return (
      <div className="rounded-lg border border-gray-200 p-3.5 space-y-2">
        <SectionHeader isCarousel={isCarousel} slideNumber={slideNumber} totalSlides={totalSlides} />
        <InlineError message={cornersError} />
      </div>
    );
  }
  if (cornersLoading && !corners) {
    return (
      <div className="rounded-lg border border-gray-200 p-3.5">
        <SectionHeader isCarousel={isCarousel} slideNumber={slideNumber} totalSlides={totalSlides} />
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
          <i className="ri-loader-4-line animate-spin text-sm"></i>
          <span>Checking the best corner for your logo...</span>
        </div>
      </div>
    );
  }
  if (!corners || !corners.has_logo || !corners.corners) return null;

  const scores = corners.corners;
  const hasTransparent = Boolean(corners.logo_transparent_url);
  const useTransparent = style.variant === 'transparent' && hasTransparent;
  const previewLogoUrl = (useTransparent ? corners.logo_transparent_url : corners.logo_url) || '';
  const brandColours = (corners.brand_colors ?? []).filter(
    (c) => !FIXED_BACKGROUNDS.some((f) => f.colour.toUpperCase() === c.toUpperCase()),
  );
  const isCustomBackground = Boolean(
    style.background &&
      ![...FIXED_BACKGROUNDS.map((f) => f.colour), ...brandColours].some(
        (c) => c.toUpperCase() === style.background!.toUpperCase(),
      ),
  );
  // Rounding shows on a background box, or on the original logo's own background.
  const showRoundedOption = Boolean(style.background) || (!useTransparent && Boolean(corners.logo_has_background));
  // The style only matters with a logo on, so 'none' never counts as a change.
  // Applying to every slide is always allowed: other slides may differ.
  const nothingToApply =
    !selected ||
    (!allSlides && selected === current && (selected === 'none' || sameLogoStyle(style, currentStyle)));

  // Preview geometry, mirroring the compositor (_logo_size/_placement): the
  // logo is a fraction of the image width, capped at a fraction of its height;
  // the margin is measured to the padded box's outer edge, drawn or not.
  const size: LogoSize = style.size ?? 'medium';
  const { widthRatio, maxHeightRatio } = LOGO_SIZES[size];
  let previewLogoW = frame ? frame.w * widthRatio : 0;
  let previewLogoH = logoAspect ? previewLogoW * logoAspect : 0;
  if (frame && logoAspect && previewLogoH > frame.h * maxHeightRatio) {
    previewLogoH = frame.h * maxHeightRatio;
    previewLogoW = previewLogoH / logoAspect;
  }
  const boxPadding = previewLogoH * LOGO_BOX_PADDING_RATIO;
  const boxRadius = style.rounded ? (previewLogoH + 2 * boxPadding) * LOGO_BOX_RADIUS_RATIO : 0;
  const logoRadius = style.rounded && !style.background ? previewLogoH * LOGO_CORNER_RADIUS_RATIO : 0;
  const previewMargin = frame ? frame.w * LOGO_MARGIN_RATIO : 0;

  const handleApply = async () => {
    if (!selected || nothingToApply) return;
    setApplying(true);
    setApplyError(null);
    try {
      const view = isCarousel
        ? await apiService.applyCarouselLogo(postId, selected, slideNumber as number, style, allSlides)
        : await apiService.applyLogo(postId, selected, style);
      onApplied(view);
    } catch (err: any) {
      setApplyError(err?.message || 'Logo placement failed. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3.5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <SectionHeader isCarousel={isCarousel} slideNumber={slideNumber} totalSlides={totalSlides} />
        {current && (
          <div className="text-xs text-gray-600 flex items-center gap-1">
            <span>
              Logo: <span className="font-semibold text-gray-900">{LOGO_POSITION_LABELS[current]}</span>
            </span>
            <span className="text-gray-300">·</span>
            <button
              type="button"
              onClick={() => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              className="text-violet-600 hover:text-violet-700 font-medium cursor-pointer"
            >
              Change
            </button>
          </div>
        )}
      </div>

      {/* Raw (logo-free) thumbnail with the four clickable corner zones + live logo overlay */}
      <div ref={setGridEl} className="relative rounded-md overflow-hidden border border-gray-200 bg-gray-50">
        <img
          src={rawImageUrl}
          alt={isCarousel ? 'Carousel end card without logo' : 'Image without logo'}
          className="w-full object-contain block"
        />

        {/* Live Interactive Logo Preview Overlay */}
        {selected && selected !== 'none' && previewLogoUrl && frame && (
          // Exactly what the composite draws: the chosen logo at the chosen
          // size, optionally on a coloured box, at the composite's margin.
          <div
            className="absolute pointer-events-none z-10 flex items-center justify-center animate-in fade-in zoom-in-95 duration-150 transition-[border-radius,background-color] duration-150"
            style={{
              [selected.startsWith('top') ? 'top' : 'bottom']: previewMargin,
              [selected.endsWith('left') ? 'left' : 'right']: previewMargin,
              padding: boxPadding,
              backgroundColor: style.background ?? undefined,
              borderRadius: style.background ? boxRadius : undefined,
            }}
          >
            <img
              key={previewLogoUrl}
              src={previewLogoUrl}
              alt="Brand Logo Live Preview"
              className="block max-w-none transition-[border-radius] duration-150"
              style={{
                width: previewLogoW,
                height: logoAspect ? previewLogoH : undefined,
                borderRadius: logoRadius || undefined,
              }}
              onLoad={(e) => {
                const { naturalWidth, naturalHeight } = e.currentTarget;
                if (naturalWidth) setLogoAspect(naturalHeight / naturalWidth);
              }}
            />
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-violet-600"></span>
            </span>
          </div>
        )}

        {LOGO_CORNERS.map((corner) => {
          const score = scores[corner];
          const isSelected = selected === corner;
          const isRecommended = corners.recommended === corner;
          const isBusy = score?.verdict === 'busy';
          return (
            <button
              key={corner}
              type="button"
              data-corner={corner}
              aria-pressed={isSelected}
              aria-label={`${LOGO_POSITION_LABELS[corner]}${isRecommended ? ' (recommended)' : ''}${isBusy ? ' — may cover content' : ''}`}
              onClick={() => handleSelectCorner(corner)}
              className={`absolute ${ZONE_CLASSES[corner]} w-[34%] h-[28%] rounded-md border-2 border-dashed flex flex-col items-center p-1 transition-all cursor-pointer ${
                isSelected
                  ? `${SELECTED_LABELS_ALIGN[corner]} border-violet-600 bg-violet-600/15 ring-2 ring-violet-500/30`
                  : isBusy
                    ? 'justify-center border-amber-400 bg-amber-100/30 hover:bg-amber-100/50'
                    : 'justify-center border-white/90 bg-black/10 hover:bg-black/20'
              }`}
            >
              {/* Selected: one compact row on the side away from the logo preview */}
              <span className={`flex items-center justify-center gap-1 ${isSelected ? 'flex-row flex-wrap' : 'flex-col'}`}>
                <span className="bg-white/95 text-gray-800 text-[10px] font-semibold px-1.5 py-0.5 rounded shadow-xs flex items-center gap-0.5">
                  {isSelected && <i className="ri-check-line text-[11px] font-bold text-violet-600"></i>}
                  {LOGO_POSITION_LABELS[corner]}
                </span>
                {isRecommended && (
                  <span className="bg-emerald-600 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full shadow-xs flex items-center gap-0.5">
                    <i className="ri-star-fill text-[8px]"></i> Recommended
                  </span>
                )}
                {isBusy && (
                  <span className="bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                    <i className="ri-error-warning-line text-[9px]"></i> May cover content
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {selected !== 'none' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-medium text-gray-800">Size</p>
            <div role="radiogroup" aria-label="Logo size" className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50">
              {(Object.keys(LOGO_SIZES) as LogoSize[]).map((option) => {
                const active = size === option;
                return (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => updateStyle({ size: option })}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors cursor-pointer ${
                      active ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-200' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {LOGO_SIZES[option].label}
                  </button>
                );
              })}
            </div>
          </div>

          {hasTransparent && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-medium text-gray-800">Logo</p>
              <div role="radiogroup" aria-label="Logo" className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50">
                {([
                  { variant: 'original', label: 'Original', url: corners.logo_url },
                  { variant: 'transparent', label: 'Transparent', url: corners.logo_transparent_url },
                ] as const).map(({ variant, label, url }) => {
                  const active = style.variant === variant;
                  return (
                    <button
                      key={variant}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => updateStyle({ variant })}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors cursor-pointer ${
                        active ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-200' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <span
                        className="w-5 h-5 rounded-sm border border-gray-200 flex items-center justify-center overflow-hidden"
                        style={{ background: 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 6px 6px' }}
                      >
                        {url && <img src={url} alt="" className="max-w-full max-h-full object-contain" />}
                      </span>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-medium text-gray-800">Background</p>
            <div role="radiogroup" aria-label="Logo background" className="flex flex-wrap items-center justify-end gap-1.5">
              <Swatch
                label="No background"
                active={!style.background}
                onClick={() => updateStyle({ background: null })}
              >
                <i className="ri-forbid-line text-sm text-gray-400"></i>
              </Swatch>
              {[...FIXED_BACKGROUNDS, ...brandColours.map((c, i) => ({ colour: c, label: `Brand colour ${i + 1}` }))].map(
                ({ colour, label }) => (
                  <Swatch
                    key={colour}
                    label={label}
                    colour={colour}
                    active={(style.background ?? '').toUpperCase() === colour.toUpperCase()}
                    onClick={() => updateStyle({ background: colour })}
                  />
                ),
              )}
              <Swatch
                label="Custom colour"
                colour={isCustomBackground ? style.background : null}
                active={isCustomBackground}
                onClick={() => colourInputRef.current?.click()}
              >
                {!isCustomBackground && <i className="ri-palette-line text-sm text-gray-500"></i>}
              </Swatch>
              <input
                ref={colourInputRef}
                type="color"
                aria-label="Custom background colour"
                className="sr-only"
                value={(style.background ?? '#FFFFFF').toLowerCase()}
                onChange={(e) => updateStyle({ background: e.target.value.toUpperCase() })}
              />
            </div>
          </div>

          {showRoundedOption && (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p id="logo-rounded-label" className="text-xs font-medium text-gray-800">Rounded corners</p>
                <p className="text-[11px] text-gray-500">
                  {style.background ? 'Round the corners of the background box.' : "Round the corners of your logo's background."}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={style.rounded}
                aria-labelledby="logo-rounded-label"
                onClick={() => updateStyle({ rounded: !style.rounded })}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40 ${
                  style.rounded ? 'bg-violet-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    style.rounded ? 'translate-x-[18px]' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          )}
        </div>
      )}

      {canApplyToAll && (
        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={applyToAllSlides}
            onChange={(e) => setApplyToAllSlides(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
          />
          <span>Apply to all {totalSlides} slides (same corner and style)</span>
        </label>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-pressed={selected === 'none'}
          onClick={() => handleSelectCorner('none')}
          className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors cursor-pointer flex items-center gap-1.5 ${
            selected === 'none'
              ? 'border-violet-600 bg-violet-50 text-violet-700'
              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <i className="ri-forbid-line text-sm"></i> No logo
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={applying || nothingToApply}
          className="flex-1 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed shadow-sm"
        >
          {applying ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm"></i>
              <span>Applying logo...</span>
            </>
          ) : selected === 'none' ? (
            <span>
              {allSlides ? 'Remove logo from all slides' : current && current !== 'none' ? 'Remove logo' : 'Keep without logo'}
            </span>
          ) : (
            <span>
              Apply logo{selected ? ` · ${LOGO_POSITION_LABELS[selected]}` : ''}
              {allSlides ? ` · all ${totalSlides} slides` : ''}
            </span>
          )}
        </button>
      </div>

      {applyError && <InlineError message={applyError} />}
    </div>
  );
}

function Swatch({
  label,
  colour,
  active,
  onClick,
  children,
}: {
  label: string;
  colour?: string | null;
  active: boolean;
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`w-6 h-6 rounded-full border flex items-center justify-center cursor-pointer transition-shadow ${
        active ? 'ring-2 ring-violet-500 ring-offset-1 border-violet-300' : 'border-gray-300 hover:ring-2 hover:ring-gray-200'
      }`}
      style={colour ? { backgroundColor: colour } : { backgroundColor: '#FFFFFF' }}
    >
      {children}
    </button>
  );
}

function SectionHeader({
  isCarousel = false,
  slideNumber,
  totalSlides,
}: {
  isCarousel?: boolean;
  slideNumber?: number;
  totalSlides?: number;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 rounded bg-violet-50 text-violet-600 flex items-center justify-center">
        <i className="ri-image-edit-line text-sm"></i>
      </div>
      <h4 className="text-sm font-semibold text-gray-900">
        Logo placement
        {isCarousel && slideNumber ? (
          <span className="text-gray-400 font-normal">
            {' '}
            · Slide {slideNumber}
            {totalSlides ? ` of ${totalSlides}` : ''}
          </span>
        ) : null}
      </h4>
    </div>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5">
      <i className="ri-error-warning-line text-sm mt-0.5 shrink-0"></i>
      <span>{message}</span>
    </div>
  );
}
